/**
 * DRBFM↔CAD linker state for CADViewerPage (TG #673): fetches the session's
 * markers, keeps only the ones anchored to the model on screen, groups
 * co-located pins, merges them with the chat markers, and owns the one-shot
 * focus that auto-opens the detail drawer for `?failure_mode=<id>`.
 *
 * Extracted verbatim from CADViewerPage — the memos, the focus effect and
 * their dependency arrays are unchanged.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDRBFMWithCAD } from '../../hooks/useDRBFMWithCAD';
import { CADMarker, CADModel } from '../../types/cad';
import { groupCADMarkersByPosition } from '../../utils/cadMarkers';

interface UseCadDrbfmOverlayOptions {
  drbfmSessionId: string | null;
  focusFailureModeId: string | null;
  selectedModel: CADModel | null;
  chatMarkers: CADMarker[];
}

export function useCadDrbfmOverlay({
  drbfmSessionId,
  focusFailureModeId,
  selectedModel,
  chatMarkers,
}: UseCadDrbfmOverlayOptions) {
  const {
    markers: drbfmMarkers,
    loading: drbfmLoading,
    error: drbfmError,
  } = useDRBFMWithCAD(drbfmSessionId);
  const [selectedMarker, setSelectedMarker] = useState<CADMarker | null>(null);

  // Only render DRBFM markers when their anchored cad_model_id matches the
  // currently displayed model. Anchors are in the source CAD's coordinate
  // system, so dropping them onto a different mesh would put pins at
  // misleading positions.
  const drbfmMarkersForCurrentModel = useMemo<CADMarker[]>(() => {
    if (!selectedModel) return [];
    return drbfmMarkers.filter((marker) => {
      const payload = marker.detailPayload as { cadAnchor?: { cadModelId?: string } } | undefined;
      const anchorModelId = payload?.cadAnchor?.cadModelId;
      return anchorModelId === selectedModel.id;
    });
  }, [drbfmMarkers, selectedModel]);

  const groupedDRBFMMarkersForCurrentModel = useMemo<CADMarker[]>(
    () => groupCADMarkersByPosition(drbfmMarkersForCurrentModel),
    [drbfmMarkersForCurrentModel],
  );

  const mergedMarkers = useMemo<CADMarker[]>(
    () => [...chatMarkers, ...groupedDRBFMMarkersForCurrentModel],
    [chatMarkers, groupedDRBFMMarkersForCurrentModel],
  );

  // When the viewer is opened via the DRBFMTable per-row 3D view button,
  // `?failure_mode=<id>` is present. Auto-open the detail drawer for that
  // anchor as soon as the matching marker shows up. One-shot: manual marker
  // clicks afterwards still drive the drawer normally (TG #673).
  const focusAppliedRef = useRef(false);
  useEffect(() => {
    if (focusAppliedRef.current) return;
    if (!focusFailureModeId) return;
    const match = drbfmMarkersForCurrentModel.find(
      (m) => m.failureModeId === focusFailureModeId,
    );
    if (match) {
      setSelectedMarker(match);
      focusAppliedRef.current = true;
    }
  }, [focusFailureModeId, drbfmMarkersForCurrentModel]);

  return {
    drbfmMarkers,
    drbfmLoading,
    drbfmError,
    drbfmMarkersForCurrentModel,
    groupedDRBFMMarkersForCurrentModel,
    mergedMarkers,
    selectedMarker,
    setSelectedMarker,
  };
}
